import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "sonner";
import {
  Check,
  X,
  Truck,
  AlertTriangle,
  Clock,
  TrendingUp,
  ArrowLeft,
  ExternalLink,
  Zap,
} from "lucide-react";

import { getSkuPlanning } from "@/lib/sku-planning.functions";
import {
  decidePurchaseSuggestion,
  confirmPurchaseSuggestion,
  adjustPurchaseSuggestion,
} from "@/lib/forecasting.functions";
import {
  explainInventoryPlanning,
  type PlanningDecision,
} from "@/lib/explain-inventory-planning";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute("/_authenticated/app/forecasting/sku/$skuId")({
  component: SkuPlanningPage,
});

function fmtMoney(cents: number | null | undefined) {
  if (cents == null) return "—";
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}
function fmtDate(d: string | null | undefined) {
  return d ? d.slice(0, 10) : "—";
}
function relDays(d: string | null | undefined): string {
  if (!d) return "—";
  const days = Math.round(
    (new Date(d).getTime() - Date.now()) / 86_400_000,
  );
  if (days === 0) return "today";
  if (days > 0) return `in ${days}d`;
  return `${Math.abs(days)}d ago`;
}

function StatTile({
  label,
  value,
  sub,
  tone = "default",
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  tone?: "default" | "warn" | "danger" | "good";
}) {
  const toneClass =
    tone === "danger"
      ? "text-destructive"
      : tone === "warn"
        ? "text-amber-600 dark:text-amber-400"
        : tone === "good"
          ? "text-emerald-600 dark:text-emerald-400"
          : "text-foreground";
  return (
    <div className="rounded-md border bg-card p-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={`mt-1 text-xl font-semibold tabular-nums ${toneClass}`}>
        {value}
      </div>
      {sub ? (
        <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>
      ) : null}
    </div>
  );
}

function SeverityBadge({ severity }: { severity: string }) {
  const tone =
    severity === "critical" || severity === "high"
      ? "destructive"
      : severity === "warn" || severity === "medium"
        ? "default"
        : "secondary";
  return (
    <Badge variant={tone as "default" | "secondary" | "destructive"}>
      {severity}
    </Badge>
  );
}

function VolatilityBadge({
  v,
}: {
  v: "stable" | "moderate" | "high" | "insufficient_history";
}) {
  if (v === "insufficient_history")
    return <Badge variant="outline">insufficient history</Badge>;
  return (
    <Badge variant={v === "high" ? "destructive" : v === "moderate" ? "default" : "secondary"}>
      {v} demand
    </Badge>
  );
}

function SkuPlanningPage() {
  const { skuId } = Route.useParams();
  const { tenantId, facilityId } = useScope();
  const qc = useQueryClient();
  const getFn = useServerFn(getSkuPlanning);
  const decideFn = useServerFn(decidePurchaseSuggestion);
  const confirmFn = useServerFn(confirmPurchaseSuggestion);
  const adjustFn = useServerFn(adjustPurchaseSuggestion);

  const { data, isLoading } = useQuery({
    queryKey: ["sku-planning", tenantId, facilityId, skuId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () =>
      getFn({
        data: {
          tenantId: tenantId!,
          facilityId: facilityId!,
          skuId,
          lookbackDays: 84,
        },
      }),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["sku-planning", tenantId, facilityId, skuId] });
    // Per-SKU decisions change the tenant-wide overview + suggestions list.
    qc.invalidateQueries({ queryKey: ["forecast-overview"] });
    qc.invalidateQueries({ queryKey: ["forecast-suggestions"] });
    qc.invalidateQueries({ queryKey: ["stockout-alerts"] });
  };

  const decide = useMutation({
    mutationFn: (vars: { id: string; status: "accepted" | "dismissed" }) =>
      decideFn({
        data: { id: vars.id, tenantId: tenantId!, status: vars.status },
      }),
    onSuccess: (_d, vars) => {
      toast.success(vars.status === "accepted" ? "Accepted" : "Dismissed");
      invalidate();
    },
    onError: (e) => toast.error(String(e)),
  });

  const confirm = useMutation({
    mutationFn: (vars: { id: string }) =>
      confirmFn({ data: { id: vars.id, tenantId: tenantId! } }),
    onSuccess: (d) => {
      toast.success(`ASN ${d.asnReference} created`, {
        description: `${d.qty} units${d.supplierName ? ` · ${d.supplierName}` : ""} · expected ${d.expectedAt.slice(0, 10)} — handed off to Receiving`,
      });
      invalidate();
    },
    onError: (e) => toast.error(String(e)),
  });

  const adjust = useMutation({
    mutationFn: (vars: {
      id: string;
      overrideQty: number | null;
      overrideSupplierName: string | null;
      overrideReason: string | null;
    }) =>
      adjustFn({
        data: {
          id: vars.id,
          tenantId: tenantId!,
          overrideQty: vars.overrideQty,
          overrideSupplierName: vars.overrideSupplierName,
          overrideReason: vars.overrideReason,
        },
      }),
    onSuccess: () => {
      toast.success("Adjustment saved");
      invalidate();
    },
    onError: (e) => toast.error(String(e)),
  });

  const [showAdjust, setShowAdjust] = useState(false);
  const [adjQty, setAdjQty] = useState<string>("");
  const [adjSupplier, setAdjSupplier] = useState<string>("");
  const [adjReason, setAdjReason] = useState<string>("");

  if (isLoading || !data) {
    return (
      <>
        <PageHeader title="SKU planning" description="Loading…" />
        <div className="px-4 sm:px-6 py-4 text-sm text-muted-foreground">Loading…</div>
      </>
    );
  }

  const { sku, currentState, recommendation, decision, demandSeries, inbound, history, stockoutAlert, signals, policy } = data;
  const explanation = explainInventoryPlanning(decision as PlanningDecision);

  // Effective values shown on the action row — override wins.
  const effectiveQty = recommendation
    ? recommendation.override_qty ?? recommendation.suggested_qty
    : null;
  const effectiveSupplier = recommendation
    ? recommendation.override_supplier_name ?? recommendation.supplier_name
    : null;
  const isAdjusted =
    !!recommendation &&
    (recommendation.override_qty != null || recommendation.override_supplier_name);

  const dosTone =
    currentState.days_of_supply == null
      ? "default"
      : currentState.days_of_supply < (policy?.lead_time_days ?? 14)
        ? "danger"
        : currentState.days_of_supply < (policy?.lead_time_days ?? 14) * 2
          ? "warn"
          : "good";

  const requiresExpedite = decision.inputs.requires_expedite;

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <span className="font-mono text-sm text-muted-foreground">{sku.sku_code}</span>
            <span>{sku.name}</span>
          </span>
        }
        description={sku.customer_name ?? undefined}
        meta={
          <Link
            to="/app/forecasting/suggestions"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" /> Back to suggestions
          </Link>
        }
      />

      <div className="px-4 sm:px-6 py-4 space-y-6">
        {/* Headline / four-question answer */}
        <Card className="border-l-4" style={{ borderLeftColor: "hsl(var(--primary))" }}>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="space-y-1">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Recommendation
                </div>
                <div className="text-lg font-semibold">{explanation.headline}</div>
                <div className="text-sm text-muted-foreground">
                  via {explanation.source}
                </div>
              </div>
              {requiresExpedite ? (
                <Badge variant="destructive" className="gap-1">
                  <Zap className="h-3 w-3" /> Expedite required
                </Badge>
              ) : null}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-sm">
              <div className="rounded-md bg-muted/40 p-2">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Why now?
                </div>
                <div className="mt-0.5">
                  {currentState.days_of_supply != null
                    ? `${currentState.days_of_supply}d of supply vs ${policy?.lead_time_days ?? "?"}d lead time`
                    : "Coverage cannot be projected — no recent demand."}
                </div>
              </div>
              <div className="rounded-md bg-muted/40 p-2">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Why this quantity?
                </div>
                <div className="mt-0.5">
                  {recommendation
                    ? `${recommendation.suggested_qty} units — ROP ${policy?.reorder_point ?? "?"}, reorder qty ${policy?.reorder_qty ?? "?"}`
                    : "No active recommendation."}
                </div>
              </div>
              <div className="rounded-md bg-muted/40 p-2">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Why this supplier?
                </div>
                <div className="mt-0.5">
                  {recommendation?.supplier_name
                    ? `${recommendation.supplier_name} (policy default)`
                    : policy?.supplier_name ?? "No supplier configured."}
                </div>
              </div>
              <div className="rounded-md bg-muted/40 p-2">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  What if I do nothing?
                </div>
                <div className="mt-0.5">
                  {currentState.projected_stockout_date
                    ? `Stockout ${fmtDate(currentState.projected_stockout_date)} (${relDays(currentState.projected_stockout_date)}).`
                    : "Coverage remains healthy at current demand."}
                </div>
              </div>
            </div>

            {recommendation ? (
              <RecommendationActions
                recommendation={recommendation}
                effectiveQty={effectiveQty!}
                effectiveSupplier={effectiveSupplier}
                isAdjusted={!!isAdjusted}
                showAdjust={showAdjust}
                onToggleAdjust={() => {
                  setShowAdjust((v) => !v);
                  setAdjQty(String(recommendation.override_qty ?? recommendation.suggested_qty));
                  setAdjSupplier(recommendation.override_supplier_name ?? recommendation.supplier_name ?? "");
                  setAdjReason(recommendation.override_reason ?? "");
                }}
                onAccept={() =>
                  decide.mutate({ id: recommendation.id, status: "accepted" })
                }
                onDismiss={() =>
                  decide.mutate({ id: recommendation.id, status: "dismissed" })
                }
                busy={decide.isPending || adjust.isPending}
                adjQty={adjQty}
                setAdjQty={setAdjQty}
                adjSupplier={adjSupplier}
                setAdjSupplier={setAdjSupplier}
                adjReason={adjReason}
                setAdjReason={setAdjReason}
                saving={adjust.isPending}
                onSaveAdjust={() => {
                  const qtyNum = Number(adjQty);
                  if (!Number.isFinite(qtyNum) || qtyNum < 1) {
                    toast.error("Quantity must be at least 1");
                    return;
                  }
                  adjust.mutate({
                    id: recommendation.id,
                    overrideQty: qtyNum,
                    overrideSupplierName: adjSupplier.trim() || null,
                    overrideReason: adjReason.trim() || null,
                  });
                  setShowAdjust(false);
                }}
                onClearAdjust={() => {
                  adjust.mutate({
                    id: recommendation.id,
                    overrideQty: null,
                    overrideSupplierName: null,
                    overrideReason: null,
                  });
                  setShowAdjust(false);
                }}
                fmtMoney={fmtMoney}
              />
            ) : null}
          </CardContent>
        </Card>

        {/* Current State */}
        <section className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Current state
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            <StatTile label="On hand" value={currentState.on_hand.toLocaleString()} />
            <StatTile label="On order" value={currentState.on_order.toLocaleString()} sub={currentState.next_inbound_at ? `next ${fmtDate(currentState.next_inbound_at)}` : "no inbound"} />
            <StatTile label="Available" value={currentState.available.toLocaleString()} />
            <StatTile
              label="Days of supply"
              value={currentState.days_of_supply ?? "—"}
              tone={dosTone}
              sub={`${decision.assumptions.demand_density.toFixed(2)}/day`}
            />
            <StatTile
              label="Projected stockout"
              value={fmtDate(currentState.projected_stockout_date)}
              sub={relDays(currentState.projected_stockout_date)}
              tone={dosTone === "danger" ? "danger" : "default"}
            />
            <StatTile
              label="Lead time"
              value={`${decision.assumptions.observed_lead_time_days ?? policy?.lead_time_days ?? "—"}d`}
              sub={`policy ${policy?.lead_time_days ?? "—"}d · safety ${policy?.min_stock_qty ?? 0}`}
            />
          </div>
        </section>

        {/* Explainability */}
        <section className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Why this recommendation
          </h2>
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="space-y-1">
                {explanation.detail.map((line, i) => (
                  <p key={i} className="text-sm">{line}</p>
                ))}
              </div>

              <Separator />

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                <div>
                  <div className="text-xs text-muted-foreground">Demand density</div>
                  <div className="font-medium">
                    {decision.assumptions.demand_density.toFixed(2)} /day
                    <span className="text-xs text-muted-foreground ml-1">
                      over {decision.assumptions.trailing_window_days}d
                    </span>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Last 7d demand</div>
                  <div className="font-medium">
                    {decision.assumptions.last_7d_daily_demand?.toFixed(2) ?? "—"} /day
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Volatility</div>
                  <div className="font-medium">
                    <VolatilityBadge v={decision.assumptions.demand_volatility ?? "insufficient_history"} />
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Lead time</div>
                  <div className="font-medium">
                    {decision.assumptions.observed_lead_time_days != null
                      ? `${decision.assumptions.observed_lead_time_days}d observed`
                      : `${decision.assumptions.policy_lead_time_days}d (policy)`}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Safety buffer</div>
                  <div className="font-medium">
                    {decision.assumptions.safety_buffer_days}d
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Inbound on-time rate</div>
                  <div className="font-medium">
                    {decision.assumptions.inbound_reliability != null
                      ? `${Math.round(decision.assumptions.inbound_reliability * 100)}%`
                      : "no history"}
                    {decision.assumptions.observed_inbound_slip_days
                      ? ` · slip ${decision.assumptions.observed_inbound_slip_days}d`
                      : ""}
                  </div>
                </div>
              </div>

              {decision.inputs.inbound_overdue ? (
                <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm">
                  <AlertTriangle className="h-4 w-4 text-destructive mt-0.5" />
                  <div>
                    <div className="font-medium text-destructive">Inbound overdue</div>
                    <div className="text-muted-foreground text-xs">
                      Next inbound expected {fmtDate(currentState.next_inbound_at)} — confirm carrier ETA before relying on it.
                    </div>
                  </div>
                </div>
              ) : null}

              {explanation.alternatives.length > 0 ? (
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                    Alternatives considered
                  </div>
                  <ul className="space-y-2">
                    {explanation.alternatives.map((a) => (
                      <li key={a.key} className="rounded-md border bg-muted/30 p-2 text-sm">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium">{a.label}</span>
                          {a.metric ? (
                            <span className="text-xs text-muted-foreground">{a.metric}</span>
                          ) : null}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">{a.rationale}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </section>

        {/* Demand & Supply timeline */}
        <section className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Demand &amp; supply
          </h2>
          <Card>
            <CardHeader className="pb-0">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <TrendingUp className="h-4 w-4" /> Shipped units · last {decision.assumptions.trailing_window_days}d
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <div className="h-48 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={demandSeries} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border/40" />
                    <XAxis
                      dataKey="day"
                      tickFormatter={(d) => (d as string).slice(5)}
                      tick={{ fontSize: 10 }}
                      minTickGap={24}
                    />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ fontSize: 12 }}
                      labelFormatter={(l) => fmtDate(l as string)}
                    />
                    <Bar dataKey="qty" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap items-center gap-3 px-2 pt-2 text-xs text-muted-foreground">
                <span>On hand <span className="font-medium text-foreground">{currentState.on_hand}</span></span>
                <span>· On order <span className="font-medium text-foreground">{currentState.on_order}</span></span>
                {currentState.projected_stockout_date ? (
                  <span>· Projected depletion <span className="font-medium text-foreground">{fmtDate(currentState.projected_stockout_date)}</span></span>
                ) : null}
                {decision.inputs.recommended_order_date ? (
                  <span>· Suggested order by <span className="font-medium text-foreground">{fmtDate(decision.inputs.recommended_order_date)}</span></span>
                ) : null}
              </div>
            </CardContent>
          </Card>

          {inbound.length > 0 ? (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Truck className="h-4 w-4" /> Open inbound
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ASN</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Expected</TableHead>
                      <TableHead className="text-right">Expected qty</TableHead>
                      <TableHead className="text-right">Received</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inbound.map((r, i) => (
                      <TableRow key={(r.asn_id ?? "") + i}>
                        <TableCell>
                          {r.asn_id ? (
                            <Link
                              to="/app/receiving/$asnId"
                              params={{ asnId: r.asn_id }}
                              className="font-mono text-xs hover:underline inline-flex items-center gap-1"
                            >
                              {r.reference} <ExternalLink className="h-3 w-3" />
                            </Link>
                          ) : (
                            <span className="font-mono text-xs">{r.reference}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs capitalize">{r.status}</TableCell>
                        <TableCell className="text-xs">
                          {fmtDate(r.expected_at)}
                          {r.expected_at && r.expected_at.slice(0, 10) < new Date().toISOString().slice(0, 10) ? (
                            <Badge variant="destructive" className="ml-1 text-[10px]">overdue</Badge>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{r.expected_qty}</TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{r.received_qty}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : null}
        </section>

        {/* Related signals */}
        <section className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Related signals
          </h2>
          <Card>
            <CardContent className="p-0">
              {signals.length === 0 && !stockoutAlert ? (
                <div className="p-4 text-sm text-muted-foreground">
                  No active planning signals for this SKU.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Signal</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>When</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stockoutAlert && stockoutAlert.status !== "resolved" ? (
                      <TableRow>
                        <TableCell className="text-sm">
                          Stockout alert — {stockoutAlert.days_of_cover}d cover, projected {fmtDate(stockoutAlert.projected_stockout_date)}
                        </TableCell>
                        <TableCell><SeverityBadge severity={stockoutAlert.severity} /></TableCell>
                        <TableCell className="text-xs capitalize">{stockoutAlert.status}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{fmtDate(stockoutAlert.updated_at)}</TableCell>
                      </TableRow>
                    ) : null}
                    {signals.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell className="text-sm">{s.signal_type.replace(/_/g, " ")}</TableCell>
                        <TableCell><SeverityBadge severity={s.severity} /></TableCell>
                        <TableCell className="text-xs capitalize">{s.status}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{fmtDate(s.occurred_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Recommendation history */}
        <section className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Recommendation history
          </h2>
          <Card>
            <CardContent className="p-0">
              {history.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">No prior recommendations.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Created</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead className="text-right">Est. cost</TableHead>
                      <TableHead>Outcome</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((h) => (
                      <TableRow key={h.id}>
                        <TableCell className="text-xs text-muted-foreground">{fmtDate(h.created_at)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs capitalize">{h.status}</Badge>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {h.override_qty != null ? (
                            <span>
                              <span className="text-muted-foreground line-through mr-1">{h.suggested_qty}</span>
                              {h.override_qty}
                            </span>
                          ) : (
                            h.suggested_qty
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {h.override_supplier_name ? (
                            <span>
                              <span className="text-muted-foreground line-through mr-1">{h.supplier_name ?? "—"}</span>
                              {h.override_supplier_name}
                            </span>
                          ) : (
                            h.supplier_name ?? "—"
                          )}
                          {h.override_reason ? (
                            <div className="text-muted-foreground italic mt-0.5" title={h.override_reason}>
                              “{h.override_reason}”
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{fmtMoney(h.est_cost_cents)}</TableCell>
                        <TableCell className="text-xs">
                          {h.asn_id ? (
                            <Link
                              to="/app/receiving/$asnId"
                              params={{ asnId: h.asn_id }}
                              className="font-mono hover:underline inline-flex items-center gap-1"
                            >
                              {h.asn_reference ?? "ASN"} <ExternalLink className="h-3 w-3" />
                            </Link>
                          ) : h.decided_at ? (
                            <span className="text-muted-foreground inline-flex items-center gap-1">
                              <Clock className="h-3 w-3" /> {fmtDate(h.decided_at)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {h.status === "accepted" ? (
                            <Button size="sm" variant="outline" disabled={confirm.isPending} onClick={() => confirm.mutate({ id: h.id })}>
                              <Truck className="h-3 w-3" /> Confirm
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </section>
      </div>
    </>
  );
}

function RecommendationActions(props: {
  recommendation: {
    id: string;
    suggested_qty: number;
    supplier_name: string | null;
    est_cost_cents: number | null;
    override_qty: number | null;
    override_supplier_name: string | null;
    override_reason: string | null;
    adjusted_at: string | null;
  };
  effectiveQty: number;
  effectiveSupplier: string | null;
  isAdjusted: boolean;
  showAdjust: boolean;
  onToggleAdjust: () => void;
  onAccept: () => void;
  onDismiss: () => void;
  busy: boolean;
  adjQty: string;
  setAdjQty: (v: string) => void;
  adjSupplier: string;
  setAdjSupplier: (v: string) => void;
  adjReason: string;
  setAdjReason: (v: string) => void;
  saving: boolean;
  onSaveAdjust: () => void;
  onClearAdjust: () => void;
  fmtMoney: (cents: number | null | undefined) => string;
}) {
  const r = props.recommendation;
  return (
    <div className="space-y-3 pt-1">
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" onClick={props.onAccept} disabled={props.busy}>
          <Check className="h-3.5 w-3.5" /> Accept
        </Button>
        <Button size="sm" variant="outline" onClick={props.onToggleAdjust} disabled={props.busy}>
          {props.showAdjust ? "Cancel adjust" : props.isAdjusted ? "Edit adjustment" : "Adjust"}
        </Button>
        <Button size="sm" variant="ghost" onClick={props.onDismiss} disabled={props.busy}>
          <X className="h-3.5 w-3.5" /> Dismiss
        </Button>
        <div className="ml-auto text-sm">
          {props.isAdjusted ? (
            <span>
              <span className="text-muted-foreground line-through mr-1">
                {r.suggested_qty} units
              </span>
              <span className="font-medium">{props.effectiveQty} units</span>
              {props.effectiveSupplier ? (
                <span className="text-muted-foreground"> · {props.effectiveSupplier}</span>
              ) : null}
              <Badge variant="secondary" className="ml-2 text-[10px]">adjusted</Badge>
            </span>
          ) : (
            <span className="text-muted-foreground">
              {r.suggested_qty} units · {props.fmtMoney(r.est_cost_cents)}
            </span>
          )}
        </div>
      </div>

      {props.isAdjusted && !props.showAdjust ? (
        <div className="rounded-md border bg-muted/30 p-2 text-xs">
          <div className="text-muted-foreground">
            CairnOS recommended <span className="font-medium text-foreground">{r.suggested_qty} units</span>
            {r.supplier_name ? <> from <span className="font-medium text-foreground">{r.supplier_name}</span></> : null}.
            Planner override: <span className="font-medium text-foreground">{props.effectiveQty} units</span>
            {props.effectiveSupplier && props.effectiveSupplier !== r.supplier_name ? (
              <> from <span className="font-medium text-foreground">{props.effectiveSupplier}</span></>
            ) : null}.
          </div>
          {r.override_reason ? (
            <div className="text-muted-foreground italic mt-1">“{r.override_reason}”</div>
          ) : null}
        </div>
      ) : null}

      {props.showAdjust ? (
        <div className="rounded-md border bg-muted/30 p-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="adj-qty" className="text-xs">
                Quantity <span className="text-muted-foreground font-normal">· system {r.suggested_qty}</span>
              </Label>
              <Input
                id="adj-qty"
                type="number"
                min={1}
                value={props.adjQty}
                onChange={(e) => props.setAdjQty(e.target.value)}
                className="h-8"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="adj-supplier" className="text-xs">
                Supplier <span className="text-muted-foreground font-normal">· system {r.supplier_name ?? "—"}</span>
              </Label>
              <Input
                id="adj-supplier"
                value={props.adjSupplier}
                onChange={(e) => props.setAdjSupplier(e.target.value)}
                placeholder="Supplier name"
                className="h-8"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="adj-reason" className="text-xs">
              Reason <span className="text-muted-foreground font-normal">· why this change</span>
            </Label>
            <Textarea
              id="adj-reason"
              value={props.adjReason}
              onChange={(e) => props.setAdjReason(e.target.value)}
              placeholder="e.g. Vendor MOQ is 500 units; switched supplier due to lead time"
              rows={2}
            />
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={props.onSaveAdjust} disabled={props.saving}>
              Save adjustment
            </Button>
            {props.isAdjusted ? (
              <Button size="sm" variant="ghost" onClick={props.onClearAdjust} disabled={props.saving}>
                Revert to system recommendation
              </Button>
            ) : null}
            <span className="ml-auto text-xs text-muted-foreground">
              Saving keeps the suggestion pending — Accept to confirm.
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
